import { groupMenuActions } from "app/components/SecondaryBar/NavBars/ConversionAnalysisNavBar/utils";
import { translate } from "app/filters/i18nFilter";
import { ConversionSegmentsUtils } from "app/pages/conversion/ConversionSegmentsUtils";
import type { FC } from "react";
import type { JSX } from "react";
import type { ISegmentsData } from "app/services/conversion/ConversionSegmentsService";
import CountryService from "app/services/CountryService";
import { EllipsisDropdownItem } from "@similarweb/ui-components";
import { SecondaryItem } from "@similarweb/ui-components";
import { ESecondaryItemType } from "@similarweb/ui-components";
import { GroupSecondaryLevel } from "@similarweb/ui-components";
import { defaultGroupItemClickHandler } from "app/components/sidebar3/secondary-side-bar/components/DigitalSuiteNavBarUtils";

interface ISegmentGroupsProps {
    country: string;
    segments: ISegmentsData;
    groups: any;
    onMenuToggle: () => void;
    onGroupClick: (group) => void;
    onSegmentClick: (groupId: string, segment, groupName: string, idx: number, isOssClick: boolean) => void;
    onAllGroupsClick: () => void;
    onCreateNewClick: () => void;
    handleMenuItemClick: any;
    selectedGroupId: string;
    selectedSegmentId: string;
}

export const SegmentsGroups: FC<ISegmentGroupsProps> = ({
    country,
    segments,
    groups,
    onMenuToggle,
    onGroupClick,
    onSegmentClick,
    onAllGroupsClick,
    onCreateNewClick,
    handleMenuItemClick,
    selectedGroupId,
    selectedSegmentId,
}) => {
    const handleGroupClick = (group) => () => {
        if (group.id === selectedGroupId && !selectedSegmentId) {
            return;
        }
        onGroupClick(group);
    };

    const handleSegmentClick =
        (groupId, segment, groupName, idx, isOssClick = false) =>
        () => {
            onSegmentClick(groupId, segment, groupName, idx, isOssClick);
        };

    const getCountriesAvailableContent = (availableCountriesCodes) => {
        if (!country) {
            return "";
        }
        const currentCountry = CountryService.getCountryById(country);

        const availableCountriesArray = [];
        availableCountriesCodes.map((availableCountryCode) => {
            availableCountriesArray.push(`${CountryService.getCountryById(availableCountryCode).text}`);
        });
        const availableCountries = availableCountriesArray.join(", ");
        return `${currentCountry?.text} is not available, try switching to ${availableCountries}`;
    };

    const menuItemsConfig = [
        {
            id: groupMenuActions.DELETE,
            iconName: "delete",
            text: translate("conversion.module.side.nav.additional.options.delete"),
            disabled: false,
        },
        {
            id: groupMenuActions.RENAME,
            iconName: "edit-icon",
            text: translate("conversion.module.side.nav.additional.options.rename"),
            disabled: false,
        },
        {
            id: groupMenuActions.ADD,
            iconName: "add",
            text: translate("conversion.module.side.nav.additional.options.add"),
            disabled: false,
        },
        {
            id: groupMenuActions.REMOVE,
            iconName: "clear-circle",
            text: translate("conversion.module.side.nav.additional.options.remove"),
            disabled: false,
        },
    ];

    const buildMenuItems = (menuItemsConfig) => (): JSX.Element[] => {
        return menuItemsConfig.map((menuItemConfig) => {
            return (
                <EllipsisDropdownItem
                    key={menuItemConfig.id}
                    id={menuItemConfig.id}
                    iconName={menuItemConfig.iconName}
                    disabled={menuItemConfig.disabled}
                >
                    {menuItemConfig.text}
                </EllipsisDropdownItem>
            );
        });
    };

    const buildSegmentItemsOfCurrentGroup = (customSegments, groupId, groupName) => {
        return customSegments?.map((customSegment, idx) => {
            const domainSegment = segments?.segmentDomains[customSegment.domain];
            const isDisabled = !customSegment.countries?.includes(parseInt(country, 10));
            const isOssDisabled = ConversionSegmentsUtils.getSegmentOssUserCountries(customSegment).length === 0;
            const segmentName =
                customSegment.segmentName && customSegment.segmentName.trim().length > 0
                    ? customSegment.segmentName
                    : null;

            return (
                <SecondaryItem
                    itemType={ESecondaryItemType.Conversion}
                    itemText={customSegment.domain}
                    key={`${customSegment.id}-${groupId}`}
                    id={groupId}
                    isSelected={customSegment.id === selectedSegmentId ? groupId === selectedGroupId : null}
                    onItemClick={handleSegmentClick(groupId, customSegment, groupName, idx)}
                    favicon={domainSegment.favicon}
                    itemSubtitleText={segmentName}
                    actionIconProps={
                        !isOssDisabled
                            ? {
                                  icon: "search-ads",
                                  onClick: handleSegmentClick(groupId, customSegment, groupName, idx, true),
                              }
                            : null
                    }
                />
            );
        });
    };

    const buildGroupItems = () => {
        return groups?.map((group, idx) => {
            const trackingGroupProps = {
                depth: 2,
                report: null,
                report_group: group.name,
                report_domain: null,
                analytics_domain: "Research/buyerJourney",
                position_order: idx + 1,
                is_dropdown_list: true,
                is_locked: false,
                tag: "none",
            };
            return (
                <GroupSecondaryLevel
                    selectedItemId={selectedGroupId}
                    itemText={group.name}
                    defaultIsOpen={group.id === selectedGroupId}
                    onGroupItemClick={defaultGroupItemClickHandler(trackingGroupProps)}
                >
                    <SecondaryItem
                        itemText="Group overview"
                        id={`group-overview-${group.name}`}
                        key={`group-overview-${group.name}`}
                        itemType={ESecondaryItemType.Report}
                        getMenuItems={buildMenuItems(menuItemsConfig)}
                        onMenuItemClick={(menuItem) => handleMenuItemClick(group.id, country, menuItem.id)()}
                        onActionClick={() => null}
                        onItemClick={handleGroupClick(group)}
                        isSelected={group.id === selectedGroupId ? selectedSegmentId === undefined : null}
                    />
                    {buildSegmentItemsOfCurrentGroup(group?.segments, group.id, group.name)}
                </GroupSecondaryLevel>
            );
        });
    };

    return <>{buildGroupItems()}</>;
};
